// src/hooks/usePremium.ts
import { logger } from '../utils/logger';
import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { Purchases, PURCHASES_ERROR_CODE, PurchasesOfferings } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

export const usePremium = () => {
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [premiumExpiresAt, setPremiumExpiresAt] = useState<string | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);
  const [isRevenueCatReady, setIsRevenueCatReady] = useState(false);
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);

  useEffect(() => {
    checkPremiumStatus();

    // Subscribe to realtime changes in Supabase
    const channel = supabase
      .channel('premium_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
        },
        (payload) => {
          // Only refresh if it's the current user's subscription
          supabase.auth.getUser().then(({ data: { user } }) => {
            if (user && payload.new && (payload.new as any).user_id === user.id) {
              checkPremiumStatus();
            }
          });
        }
      )
      .subscribe();

    // Listen to RevenueCat customer info updates
    const setupRevenueCatListener = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          Purchases.addCustomerInfoUpdateListener((info: any) => {
            if (info && info.customerInfo) {
              syncRevenueCatToSupabase(info.customerInfo);
            }
          });
        }
      } catch (error) {
        logger.error('Error setting up RevenueCat listener:', error);
      }
    };

    setupRevenueCatListener();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setIsPremium(false);
        setLoading(false);
        return;
      }

      // Check if RevenueCat is available
      let revenueCatStatus = false;
      if (Capacitor.isNativePlatform()) {
        try {
          logger.log('🔍 Checking RevenueCat availability on mobile platform...');
          // Check if RevenueCat is configured
          const customerInfo = await Purchases.getCustomerInfo();
          revenueCatStatus = true;
          setIsRevenueCatReady(true);
          logger.log('✅ RevenueCat is ready and configured');

          // Sync RevenueCat status to Supabase
          await syncRevenueCatToSupabase(customerInfo.customerInfo);

          // Load offerings
          const offeringsResult = await Purchases.getOfferings();
          setOfferings(offeringsResult);

          // Log detailed offerings info for debugging
          if (offeringsResult?.current) {
            logger.log('[RevenueCat] Current offering:', offeringsResult.current);
            logger.log('[RevenueCat] Available packages:', {
              monthly: offeringsResult.current.monthly ? 'Yes' : 'No',
              annual: offeringsResult.current.annual ? 'Yes' : 'No',
              availablePackages: offeringsResult.current.availablePackages?.length || 0
            });

            // Log all available packages for debugging
            if (offeringsResult.current.availablePackages) {
              offeringsResult.current.availablePackages.forEach((pkg: any) => {
                logger.log('[RevenueCat] Package:', {
                  identifier: pkg.identifier,
                  packageType: pkg.packageType,
                  product: pkg.product?.identifier
                });
              });
            }
          } else {
            logger.warn('[RevenueCat] No current offering available');
          }
        } catch (error: any) {
          logger.error('❌ RevenueCat not available:', error.message || error);
          logger.log('💡 Make sure API keys are set in .env and RevenueCat is initialized in App.tsx');
          setIsRevenueCatReady(false);
        }
      } else {
        logger.log('🌐 Running on web - RevenueCat only works on mobile platforms');
        setIsRevenueCatReady(false);
      }

      // Always check Supabase as the source of truth
      const { data, error } = await supabase
        .from('subscriptions')
        .select('is_premium, premium_expires_at, subscription_type')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If no subscription record exists, user is not premium
        if (error.code === 'PGRST116') {
          setIsPremium(false);
          setLoading(false);
          return;
        }
        logger.error('Error checking premium status:', error);
        setIsPremium(false);
        setLoading(false);
        return;
      }

      if (data) {
        // Check if premium is active and not expired
        const isActive = data.is_premium === true;
        const isLifetime = data.subscription_type === 'lifetime';
        const notExpired = isLifetime || !data.premium_expires_at ||
          new Date(data.premium_expires_at) > new Date();

        const hasPremium = isActive && notExpired;
        setIsPremium(hasPremium);
        setPremiumExpiresAt(data.premium_expires_at);
        setSubscriptionType(data.subscription_type);
      } else {
        setIsPremium(false);
      }

      setLoading(false);
    } catch (error) {
      logger.error('Error in checkPremiumStatus:', error);
      setIsPremium(false);
      setLoading(false);
    }
  };

  const syncRevenueCatToSupabase = async (customerInfo: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if user has active entitlement
      const hasActivePremium = customerInfo.entitlements?.active?.['premium'] !== undefined;

      if (hasActivePremium) {
        const entitlement = customerInfo.entitlements.active['premium'];
        const expirationDate = entitlement.expiresDate;
        const productIdentifier = entitlement.productIdentifier;

        // Determine subscription type from product identifier
        let subscriptionType = 'monthly';
        if (productIdentifier.includes('annual') || productIdentifier.includes('yearly')) {
          subscriptionType = 'yearly';
        } else if (productIdentifier.includes('lifetime')) {
          subscriptionType = 'lifetime';
        }

        // Update or create subscription record
        const { error } = await supabase
          .from('subscriptions')
          .upsert({
            user_id: user.id,
            is_premium: true,
            premium_expires_at: expirationDate,
            subscription_type: subscriptionType,
            revenue_cat_user_id: customerInfo.originalAppUserId,
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'user_id'
          });

        if (error) {
          logger.error('Error syncing to Supabase:', error);
        } else {
          logger.log('Successfully synced RevenueCat to Supabase');
          // Refresh premium status
          await checkPremiumStatus();
        }
      } else {
        // User doesn't have active subscription
        const { error } = await supabase
          .from('subscriptions')
          .upsert({
            user_id: user.id,
            is_premium: false,
            premium_expires_at: null,
            subscription_type: 'none',
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'user_id'
          });

        if (error) {
          logger.error('Error updating Supabase:', error);
        }
      }
    } catch (error) {
      logger.error('Error in syncRevenueCatToSupabase:', error);
    }
  };

  const purchasePackage = async (packageType: 'monthly' | 'annual') => {
    if (!isRevenueCatReady) {
      throw new Error('Subscriptions launching soon! Contact support for early access.');
    }

    try {
      if (!offerings?.current) {
        throw new Error('No packages available. Please try again later.');
      }

      // Get the appropriate package - try named packages first
      let selectedPackage = packageType === 'annual'
        ? offerings.current.annual
        : offerings.current.monthly;

      // If named package not available, try to find it in availablePackages
      if (!selectedPackage && offerings.current.availablePackages) {
        logger.warn(`[RevenueCat] Named ${packageType} package not found, searching availablePackages...`);

        // Map package types
        const packageTypeMap: Record<string, string> = {
          'monthly': 'MONTHLY',
          'annual': 'ANNUAL'
        };

        selectedPackage = offerings.current.availablePackages.find((pkg: any) =>
          pkg.packageType === packageTypeMap[packageType] ||
          pkg.identifier?.toLowerCase().includes(packageType)
        ) || null;

        if (selectedPackage) {
          logger.log(`[RevenueCat] Found ${packageType} package in availablePackages:`, selectedPackage.identifier);
        }
      }

      if (!selectedPackage) {
        logger.error('[RevenueCat] Package not available:', {
          requestedType: packageType,
          monthly: offerings.current.monthly ? 'available' : 'missing',
          annual: offerings.current.annual ? 'available' : 'missing',
          availablePackagesCount: offerings.current.availablePackages?.length || 0
        });
        throw new Error(`${packageType} package not available`);
      }

      // Make the purchase
      const purchaseResult = await Purchases.purchasePackage({
        aPackage: selectedPackage,
      });

      // Sync to Supabase
      await syncRevenueCatToSupabase(purchaseResult.customerInfo);

      return true;
    } catch (error: any) {
      // Handle user cancellation
      if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        throw new Error('Purchase cancelled');
      }

      logger.error('Purchase error:', error);
      throw new Error(error.message || 'Purchase failed. Please try again.');
    }
  };

  const restorePurchases = async () => {
    if (!isRevenueCatReady) {
      // If RevenueCat not ready, just recheck Supabase
      await checkPremiumStatus();
      return isPremium;
    }

    try {
      const restoreResult = await Purchases.restorePurchases();

      // Sync to Supabase
      await syncRevenueCatToSupabase(restoreResult.customerInfo);

      return restoreResult.customerInfo.entitlements?.active?.['premium'] !== undefined;
    } catch (error) {
      logger.error('Restore error:', error);
      throw new Error('Failed to restore purchases. Please try again.');
    }
  };

  return {
    isPremium,
    loading,
    premiumExpiresAt,
    subscriptionType,
    offerings,
    isRevenueCatReady,
    purchasePackage,
    restorePurchases,
  };
};
